import { adminRequest } from "./client";
import apiClient from "./axios";

export const adminLogin = (email, password) =>
  adminRequest("/auth/admin/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });

export const adminVerify2FA = (challengeId, otp) =>
  adminRequest("/auth/admin/login/verify-otp", {
    method: "POST",
    body: JSON.stringify({ challengeId, otp }),
  });

export const adminResend2FA = () =>
  adminRequest("/auth/admin/login", { method: "POST" });

export const adminValidateSetupToken = (token) =>
  adminRequest(`/auth/admin/validate-setup-token?token=${token}`);

export const adminSetup = (payload) =>
  adminRequest("/auth/admin/setup", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const adminValidateInviteToken = (token) =>
  Promise.resolve({ valid: !!token, email: "" });

export const adminRegister = ({ token, inviteId, email, name, password }) =>
  adminRequest("/auth/admin/register", {
    method: "POST",
    body: JSON.stringify({
      email: email ?? "",
      password,
      inviteId: inviteId ?? token ?? "",
      inviteToken: token ?? "",
    }),
  });

export const adminInvite = async (email) => {
  const res = await adminRequest("/auth/admin/invites", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
  return res?.data ?? res ?? {};
};

// GET /api/auth/admin/invites does not exist in the backend API spec.
// The only invites endpoint is POST (to send an invite). There is no way
// to list pending invites — return an empty array so the UI renders
// "No pending invites." instead of throwing a 404 that pollutes the console.
export const adminGetInvites = async () => [];

export const adminGetStats = async () => {
  const res = await adminRequest("/admin/stats");
  return res?.data ?? res ?? {};
};

// Platform-wide revenue overview — GET /admin/revenue
// Combines staking fee revenue, provider-reported offerwall payments
// (AdGem + CPX), and advertiser (ad) payments. Amounts stay separated
// by currency/provider rather than being summed into one number, since
// they're denominated differently (CLT, USD, ETH, CLT again for ads).
export const adminGetRevenueOverview = async () => {
  const res = await adminRequest("/admin/revenue");
  return res?.data ?? res ?? {};
};

// Update the staking fee rate — PATCH /admin/revenue/staking-fee
// ratePercent is 0-100; this is the % of recorded stake amounts counted
// as platform staking revenue (used server-side in adminGetRevenueOverview's
// staking.revenue calculation).
export const adminUpdateStakingFeeRate = (ratePercent) =>
  adminRequest("/admin/revenue/staking-fee", {
    method: "PATCH",
    body: JSON.stringify({ stakingFeeRatePercent: Number(ratePercent) }),
  });

// Platform users live under /users (UsersController), NOT /admin/users.
// Real route: GET /api/users?search=&filter=&page=&limit=
// Actual response is wrapped: { success: true, data: { data: User[], meta: {...} } }
// (the success/data envelope was added on the backend), so unwrap one level
// here to give callers the plain { data: User[], meta } shape they expect.
export const adminGetUsers = async ({ search = "", filter = "all", page = 1, limit = 10 } = {}) => {
  const res = await adminRequest(
    `/users?search=${encodeURIComponent(search)}&filter=${filter}&page=${page}&limit=${limit}`,
  );
  return res?.data ?? res;
};

export const adminGetUserById = (userId) => adminRequest(`/users/${userId}`);

export const adminDeleteUser = (userId) =>
  adminRequest(`/users/${userId}`, { method: "DELETE" });

export const adminSuspendUser = (userId) =>
  adminRequest(`/users/${userId}/suspend`, { method: "POST" });

export const adminUnsuspendUser = (userId) =>
  adminRequest(`/users/${userId}/unsuspend`, { method: "POST" });

// Community moderation — mute/unmute a user and view the moderation
// audit trail. Routes confirmed in the OpenAPI spec; response shapes
// weren't documented there (empty schemas), so we unwrap defensively the
// same way as adminGetUsers in case the backend wraps responses in a
// { success, data } envelope.
export const adminMuteUser = async (userId, days, reason) => {
  const res = await adminRequest(`/admin/community/users/${userId}/mute`, {
    method: "PATCH",
    body: JSON.stringify({ days, reason }),
  });
  return res?.data ?? res ?? {};
};

export const adminUnmuteUser = async (userId) => {
  const res = await adminRequest(`/admin/community/users/${userId}/unmute`, { method: "PATCH" });
  return res?.data ?? res ?? {};
};

export const adminGetModerationHistory = async () => {
  const res = await adminRequest("/admin/community/moderation-history");
  return Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
};

// NOTE: there is no PATCH /users/:id endpoint on the backend right now,
// so this still points at a route that doesn't exist. Left as-is per
// "no backend fix" — flag to Joshua if you need profile-field edits
// from this screen (display name, tier override, etc).
export const adminUpdateUser = (userId, payload) =>
  adminRequest(`/admin/users/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });

export const adminGetCampaigns = () => adminRequest("/admin/campaigns");

export const adminUpdateCampaign = (campaignId, payload) =>
  adminRequest(`/admin/campaigns/${campaignId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });

export const adminDeleteCampaign = (campaignId) =>
  adminRequest(`/admin/campaigns/${campaignId}`, { method: "DELETE" });

export const adminCreateCampaign = (payload) =>
  adminRequest("/admin/campaigns", {
    method: "POST",
    body: JSON.stringify(payload),
  });

// Admin TEAM management (AdminController) — different from platform users above.
export const adminGetTeam = () => adminRequest("/admin/all");

// CPT & CIS manual adjustment — PATCH /admin/users/cpt/add|reduce, /admin/users/cis/add|subtract
// Payloads: { profileId, amount, reason? }
export const adminAddCPT = (profileId, amount, reason) =>
  adminRequest("/admin/users/cpt/add", {
    method: "PATCH",
    body: JSON.stringify({ profileId, amount, reason }),
  });

export const adminReduceCPT = (profileId, amount, reason) =>
  adminRequest("/admin/users/cpt/reduce", {
    method: "PATCH",
    body: JSON.stringify({ profileId, amount, reason }),
  });

export const adminAddCisScore = (profileId, amount) =>
  adminRequest("/admin/users/cis/add", {
    method: "PATCH",
    body: JSON.stringify({ profileId, amount }),
  });

export const adminSubtractCisScore = (profileId, amount) =>
  adminRequest("/admin/users/cis/subtract", {
    method: "PATCH",
    body: JSON.stringify({ profileId, amount }),
  });

// badge: 'gold' | 'blue' | 'grey' | 'none' (none removes an existing badge)
export const adminAssignBadge = (profileId, badge) =>
  adminRequest("/admin/users/badge", {
    method: "PATCH",
    body: JSON.stringify({ profileId, badge }),
  });

export const adminUpdateTeamMember = (adminId, payload) =>
  adminRequest(`/admin/${adminId}/update`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });

// Super admin only — PATCH /admin/:id/cpt-permission
export const adminSetCptPermission = (adminId, canDistributeCpt) =>
  adminRequest(`/admin/${adminId}/cpt-permission`, {
    method: "PATCH",
    body: JSON.stringify({ canDistributeCpt }),
  });

export const adminRemoveTeamMember = (adminId) =>
  adminRequest(`/admin/${adminId}/delete`, { method: "DELETE" });

export const adminGetPolicies = () =>
  adminRequest("/settings/policies");

export const adminUpdatePolicy = (key, content) =>
  adminRequest("/settings/policies", {
    method: "PATCH",
    body: JSON.stringify({ key, content }),
  });

export const adminAddPolicyCategory = (label, icon) =>
  adminRequest("/settings/policies/categories", {
    method: "PATCH",
    body: JSON.stringify({ label, icon }),
  });

export const adminDeletePolicyCategory = (key) =>
  adminRequest(`/settings/policies/categories/${key}`, { method: "DELETE" });

// Platform Config — anti-farming settings, GET/PATCH /settings/anti-farming
// IMPORTANT: the backend merges shallowly at the top level only
// ({ ...current, ...dto.value }), so a partial { post: { cooldownMinutes: 1 } }
// would wipe out the rest of that section's fields. Always send the FULL
// settings object (post, comment, spam — all fields) on every save.
export const adminGetAntiFarmingSettings = async () => {
  const res = await adminRequest("/settings/anti-farming");
  return res?.data ?? res ?? {};
};

export const adminUpdateAntiFarmingSettings = async (value) => {
  const res = await adminRequest("/settings/anti-farming", {
    method: "PATCH",
    body: JSON.stringify({ value }),
  });
  return res?.data ?? res ?? {};
};

export const adminGetAllSettings = async () => {
  const res = await adminRequest("/settings");
  return res?.data ?? res ?? {};
};

export const adminUpdatePlatformInfo = async (payload) => {
  const res = await adminRequest("/settings/platform-info", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return res?.data ?? res ?? {};
};

export const adminUpdateMaintenanceMode = async (enabled) => {
  const res = await adminRequest("/settings/maintenance-mode", {
    method: "PATCH",
    body: JSON.stringify({ enabled }),
  });
  return res?.data ?? res ?? {};
};

export const adminUpdateCisWeights = async (payload) => {
  const res = await adminRequest("/settings/cis-weights", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return res?.data ?? res ?? {};
};

export const adminUpdateCisRewards = async (payload) => {
  const res = await adminRequest("/settings/cis-rewards", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return res?.data ?? res ?? {};
};

export const adminUpdateStakingSettings = async (payload) => {
  const res = await adminRequest("/settings/staking-settings", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return res?.data ?? res ?? {};
};

export const adminUpdateReferralRewards = async (payload) => {
  const res = await adminRequest("/settings/referral-rewards", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return res?.data ?? res ?? {};
};

export const adminUpdateFeatureFlags = async (payload) => {
  const res = await adminRequest("/settings/feature-flags", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return res?.data ?? res ?? {};
};

// Referral analytics — GET /admin/referral-analytics, /admin/referral-analytics/all-users,
// /admin/referrals/user/:profileId
export const adminGetReferralOverview = async () => {
  const res = await adminRequest("/admin/referral-analytics");
  return res?.data ?? res ?? {};
};

export const adminGetReferralsPerUser = async () => {
  const res = await adminRequest("/admin/referral-analytics/all-users");
  return Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
};

export const adminGetUserReferrals = async (profileId) => {
  const res = await adminRequest(`/admin/referrals/user/${profileId}`);
  return Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
};

// Reward seasons — GET/POST /reward-season, GET/PATCH/DELETE /reward-season/:id
// NOTE: as of this writing, GET /reward-season/:id and PATCH /reward-season/:id
// throw a 500 on the backend (they request a 'rewardTiers' relation that doesn't
// exist on the RewardSeason entity), and POST/PATCH silently fail to persist
// seasonName (the service assigns dto.seasonName to a 'name' property that
// doesn't exist on the entity). Flagged to Joshua — list/create/delete still work.
export const adminGetSeasons = async () => {
  const res = await adminRequest("/reward-season");
  return Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
};

export const adminGetSeasonById = async (seasonId) => {
  const res = await adminRequest(`/reward-season/${seasonId}`);
  return res?.data ?? res ?? {};
};

export const adminCreateSeason = async ({ seasonName, description, startDate, endDate }) => {
  const res = await adminRequest("/reward-season", {
    method: "POST",
    body: JSON.stringify({ seasonName, description, startDate, endDate }),
  });
  return res?.data ?? res ?? {};
};

export const adminUpdateSeason = async (seasonId, payload) => {
  const res = await adminRequest(`/reward-season/${seasonId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return res?.data ?? res ?? {};
};

export const adminDeleteSeason = (seasonId) =>
  adminRequest(`/reward-season/${seasonId}`, { method: "DELETE" });

export const adminActivateSeason = async (seasonId) => {
  const res = await adminRequest(`/reward-season/${seasonId}/activate`, {
    method: "PATCH",
  });
  return res?.data ?? res ?? {};
};

export const adminEndSeason = async (seasonId) => {
  const res = await adminRequest(`/reward-season/${seasonId}/end`, {
    method: "POST",
  });
  return res?.data ?? res ?? {};
};

export const adminGetSeasonStats = async (seasonId) => {
  const res = await adminRequest(`/reward-season/${seasonId}/stats`);
  return res?.data ?? res ?? {};
};

export const adminSaveSeasonConversionConfig = async (seasonId, payload) => {
  const res = await adminRequest(`/reward-season/${seasonId}/conversion-config`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return res?.data ?? res ?? {};
};

export const adminGetSeasonConversionConfig = async (seasonId) => {
  const res = await adminRequest(`/reward-season/${seasonId}/conversion-config`);
  return res?.data ?? res ?? null;
};

export const adminGetSeasonLeaderboardStatus = async (seasonId) => {
  const res = await adminRequest(`/admin/season-leaderboard/${seasonId}/status`);
  return res?.data ?? res ?? { uploaded: false, rowCount: 0, claimOpen: false, uploadedAt: null };
};

export const adminGetPayoutConfig = async (seasonId) => {
  const res = await adminRequest(`/admin/season-leaderboard/${seasonId}/payout-config`);
  return res?.data ?? res ?? { payoutTiers: null, cptPriceUsd: null };
};

export const adminSetPayoutConfig = async (seasonId, payoutTiers, cptPriceUsd) => {
  const res = await adminRequest(`/admin/season-leaderboard/${seasonId}/payout-config`, {
    method: 'PATCH',
    body: JSON.stringify({ payoutTiers, cptPriceUsd }),
  });
  return res?.data ?? res ?? {};
};

export const adminUploadSeasonSnapshot = async (seasonId, entries) => {
  const res = await adminRequest(`/admin/season-leaderboard/${seasonId}/snapshot`, {
    method: "POST",
    body: JSON.stringify({ entries }),
  });
  return res?.data ?? res ?? {};
};

export const adminClearSeasonSnapshot = (seasonId) =>
  adminRequest(`/admin/season-leaderboard/${seasonId}/snapshot`, { method: "DELETE" });

export const adminTriggerSeasonPayout = async (seasonId) => {
  const res = await adminRequest(`/admin/season-leaderboard/${seasonId}/trigger-payout`, {
    method: "POST",
  });
  return res?.data ?? res ?? {};
};

export const adminGetRegistrationProgress = async (seasonId) => {
  const res = await adminRequest(`/admin/season-leaderboard/${seasonId}/registration-status`);
  return res?.data ?? res ?? { eligible: 0, registered: 0 };
};

// Triggers a CSV download (address,amount) of everyone who's registered a
// wallet and hasn't been paid yet — paste straight into Disperse.
export const adminDownloadPayoutExport = async (seasonId) => {
  const res = await apiClient.get(
    `/admin/season-leaderboard/${seasonId}/payout-export`,
    { responseType: "blob" },
  );
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = "payout-export.csv";
  a.click();
  URL.revokeObjectURL(url);
};

// ── Auth: forgot / reset password ────────────────────────────────────────────
export const adminForgotPassword = (email) =>
  adminRequest("/auth/admin/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
  });

export const adminResetPassword = (id, token, password) =>
  adminRequest("/auth/admin/reset-password", {
    method: "POST",
    body: JSON.stringify({ id, token, password }),
  });

// ── Admin: delete post / comment ─────────────────────────────────────────────
export const adminDeletePost = (postId) =>
  adminRequest(`/admin/posts/${postId}`, { method: "DELETE" });

export const adminDeleteComment = (commentId) =>
  adminRequest(`/admin/comments/${commentId}`, { method: "DELETE" });

// ── Announcements ─────────────────────────────────────────────────────────────
export const adminGetAnnouncements = async () => {
  const res = await adminRequest("/announcements");
  return Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
};

export const adminGetAnnouncementById = async (id) => {
  const res = await adminRequest(`/announcements/${id}`);
  return res?.data ?? res ?? {};
};

export const adminCreateAnnouncement = async (payload) => {
  const res = await adminRequest("/announcements", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return res?.data ?? res ?? {};
};

export const adminUpdateAnnouncement = async (id, payload) => {
  const res = await adminRequest(`/announcements/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return res?.data ?? res ?? {};
};

export const adminDeleteAnnouncement = (id) =>
  adminRequest(`/announcements/${id}`, { method: "DELETE" });

// ── Categories ────────────────────────────────────────────────────────────────
export const adminGetCategories = async () => {
  const res = await adminRequest("/categories");
  return Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
};

export const adminGetCategoryById = async (id) => {
  const res = await adminRequest(`/categories/${id}`);
  return res?.data ?? res ?? {};
};

export const adminCreateCategory = async (name) => {
  const res = await adminRequest("/categories", {
    method: "POST",
    body: JSON.stringify({ name }),
  });
  return res?.data ?? res ?? {};
};

// ── Offerwall Management ───────────────────────────────────────────────────────
// provider is 'cpx' | 'adgem'. flagged is optional (true/false) to filter.
export const adminGetOfferwallCompletions = async ({ provider, flagged } = {}) => {
  const params = new URLSearchParams();
  if (provider) params.set("provider", provider);
  if (flagged !== undefined) params.set("flagged", String(flagged));
  const qs = params.toString();
  const res = await adminRequest(`/marketplace/completions${qs ? `?${qs}` : ""}`);
  return Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
};

export const adminGetOfferwallRevenue = async () => {
  const res = await adminRequest("/marketplace/completions/revenue");
  return res?.data ?? res ?? { adgem: 0, cpx: 0, total: 0 };
};

export const adminFlagCompletion = (provider, id, reason) =>
  adminRequest(`/marketplace/completions/${provider}/${id}/flag`, {
    method: "PATCH",
    body: JSON.stringify({ reason }),
  });

export const adminVoidCompletion = (provider, id) =>
  adminRequest(`/marketplace/completions/${provider}/${id}/void`, {
    method: "PATCH",
  });

// Platform-wide admin audit log — every admin-triggered action that
// changes user state (post/comment deletes, mutes, CPT/CIS manual
// adjustments). Backend: GET /admin/audit-logs
export const adminGetAuditLogs = async ({
  moderatorId = "",
  targetUserId = "",
  action = "",
  from = "",
  to = "",
  offset = 0,
  limit = 25,
} = {}) => {
  const params = new URLSearchParams();
  if (moderatorId) params.set("moderatorId", moderatorId);
  if (targetUserId) params.set("targetUserId", targetUserId);
  if (action) params.set("action", action);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  params.set("offset", String(offset));
  params.set("limit", String(limit));

  const res = await adminRequest(`/admin/audit-logs?${params.toString()}`);
  return res?.data ?? res ?? { total: 0, rows: [] };
};

// ── Social Tasks (Admin) ────────────────────────────────────────────────────
// Backend: src/modules/tasks/tasks.controller.ts (all under /tasks/admin/*)

export const adminGetTasks = async (status) => {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  const res = await adminRequest(`/tasks/admin${qs}`);
  return Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
};

export const adminCreateTask = (payload) =>
  adminRequest("/tasks/admin", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const adminUpdateTask = (taskId, payload) =>
  adminRequest(`/tasks/admin/${taskId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });

export const adminDeactivateTask = (taskId) =>
  adminRequest(`/tasks/admin/${taskId}/deactivate`, { method: "PATCH" });

export const adminDeleteTask = (taskId) =>
  adminRequest(`/tasks/admin/${taskId}`, { method: "DELETE" });

export const adminGetTaskStats = async (taskId) => {
  const res = await adminRequest(`/tasks/admin/${taskId}/stats`);
  return res?.data ?? res ?? {};
};

export const adminManualCompleteTask = (taskId, profileId, note) =>
  adminRequest(`/tasks/admin/${taskId}/manual-complete`, {
    method: "POST",
    body: JSON.stringify({ profileId, note }),
  });

export const adminGetTaskSubmissions = async ({ status, taskId } = {}) => {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (taskId) params.set("taskId", taskId);
  const qs = params.toString();
  const res = await adminRequest(`/tasks/admin/submissions${qs ? `?${qs}` : ""}`);
  return Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
};

export const adminApproveTaskSubmission = (submissionId) =>
  adminRequest(`/tasks/admin/submissions/${submissionId}/approve`, {
    method: "PATCH",
  });

export const adminRejectTaskSubmission = (submissionId, reason) =>
  adminRequest(`/tasks/admin/submissions/${submissionId}/reject`, {
    method: "PATCH",
    body: JSON.stringify({ reason }),
  });
